import { useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useChatRedirectStore } from '../../store/chatRedirectStore';
import { useAuthStore } from '../../auth/authStore';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { ConfidenceBadge } from '../common/ConfidenceBadge';
import { FeedbackButtons } from '../feedback/FeedbackButtons';
import { ClarifyingQuestions } from './ClarifyingQuestions';
import { SourceCard } from '../sources/SourceCard';
import { EraxResponse } from '../erax/EraxResponse';
import { JiraTicketPanel } from '../jira/JiraTicketPanel';
import { EventTimePanel } from '../event-time/EventTimePanel';
import { SchemaViewer } from './SchemaViewer';
import { User, Bot, ExternalLink, ShieldAlert, ShieldCheck, ChevronDown, ChevronRight } from 'lucide-react';
import type { Message as MessageType } from '../../types';

interface MessageProps {
  message: MessageType;
  onClarifyingQuestionClick?: (question: string) => void;
}

/**
 * Renders a single chat message with avatar, timestamp, markdown content,
 * source citations, confidence badge, and feedback controls.
 */
export function Message({ message, onClarifyingQuestionClick }: MessageProps) {
  const { setSelectedSource, viewMode, setViewMode, setSuggestedInput } = useChatStore();
  const userEmail = useAuthStore((s) => s.user?.email);
  // The chat username is the part of the login email before '@' (e.g.
  // "adamian2" for adamian2@gmail.com) — same convention as the account menu.
  const userLabel = userEmail?.split('@')[0] || 'You';
  const isUser = message.role === 'user';
  const isClarification = !isUser && message.needsClarification;
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [pipelineExpanded, setPipelineExpanded] = useState(false);

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ERAX data query responses get their own dedicated renderer
  if (!isUser && message.queryType === 'data_query') {
    return <EraxResponse message={message} />;
  }

  // JIRA ticket responses get their own dedicated renderer
  if (!isUser && message.jiraMeta) {
    return <JiraTicketPanel message={message} />;
  }

  // Chat has no per-ticket data — a mentioned ticket key routes the user to
  // Issue Analysis (support) or CR Analysis (change requests) instead of
  // running the RAG pipeline against it.
  if (!isUser && message.queryType === 'ticket_key_redirect') {
    const ticketKey = message.ticketKeys?.[0];
    return (
      <div className="flex gap-2.5 animate-fade-in">
        <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-gray-100">
          <Bot size={13} className="text-gray-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-xs font-medium text-gray-700">Assistant</span>
            <span className="text-[10px] text-gray-400">{formatTime(message.timestamp)}</span>
          </div>
          <div className="text-sm text-gray-800">
            This chat is about LRP application functionality, not a specific ticket.
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={() => {
                if (ticketKey) useChatRedirectStore.getState().setPendingTicketKey(ticketKey);
                window.location.hash = '#/issue-analysis';
              }}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100"
            >
              <ExternalLink size={12} />
              {ticketKey ? `Open ${ticketKey} in Issue Analysis` : 'Open Issue Analysis'} (support)
            </button>
            <button
              onClick={() => { window.location.hash = '#/cr-analysis'; }}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100"
            >
              <ExternalLink size={12} />
              Open CR Analysis (change requests)
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Event timeline responses get their own dedicated renderer
  if (!isUser && message.eventTimeMeta) {
    return <EventTimePanel message={message} />;
  }

  // Schema query responses get their own dedicated renderer. The schema is
  // resolved and fetched server-side (Neo4j has the real table + module names),
  // then embedded in the response metadata — no fragile client-side extraction.
  if (!isUser && message.queryType === 'schema_query' && message.schemaData) {
    return (
      <div className="flex gap-2.5 animate-fade-in">
        <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-gray-100">
          <Bot size={13} className="text-gray-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-xs font-medium text-gray-700">Assistant</span>
            <span className="text-[10px] text-gray-400">
              {formatTime(message.timestamp)}
            </span>
          </div>
          <SchemaViewer schema={message.schemaData} />
          {/* The answer text is usually just a one-line summary of the card —
              but the Developer impact route appends Compass's code-graph
              explanation to it. Render the content when it carries more than
              that first line, or the appended section silently vanishes
              (live bug 2026-08-16: backend logged success, user saw only
              the table). */}
          {message.content.includes('\n\n') && (
            <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
              <MarkdownRenderer content={message.content} sources={message.sources} />
            </div>
          )}
          <FeedbackButtons messageId={message.id} currentFeedback={message.feedbackGiven} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex gap-2.5 animate-fade-in ${isUser ? 'flex-row-reverse' : ''}`}
      role="article"
      aria-label={`${isUser ? 'Your' : 'Assistant'} message`}
    >
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
          isUser ? 'bg-primary-500' : 'bg-gray-100'
        }`}
      >
        {isUser ? (
          <User size={13} className="text-white" />
        ) : (
          <Bot size={13} className="text-gray-600" />
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 min-w-0 ${isUser ? 'text-right' : ''}`}>
        {/* Header */}
        <div
          className={`flex items-center gap-1.5 mb-0.5 ${
            isUser ? 'justify-end' : ''
          }`}
        >
          <span className="text-xs font-medium text-gray-700">
            {isUser ? userLabel : 'Assistant'}
          </span>
          <span className="text-[10px] text-gray-400">
            {formatTime(message.timestamp)}
          </span>
          {!isUser && !isClarification && message.confidenceLevel && (
            <ConfidenceBadge level={message.confidenceLevel} score={message.confidence} />
          )}
        </div>

        {/* Message bubble */}
        <div
          className={`inline-block max-w-full rounded-2xl px-3 py-2 text-[13px] leading-snug ${
            isUser
              ? 'bg-primary-500 text-white rounded-tr-md'
              : 'bg-gray-100 text-gray-800 rounded-tl-md'
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap text-left">{message.content}</p>
          ) : (
            <div className="markdown-content">
              <MarkdownRenderer content={message.content} sources={message.sources} />
            </div>
          )}
        </div>

        {/* Guardrails feedback banner */}
        {!isUser && message.guardrails?.active && (
          <div className="mt-1 space-y-0.5">
            {message.guardrails.blocked && (
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-50 text-red-700 text-[11px]">
                <ShieldAlert size={11} />
                <span>
                  Blocked by guardrails
                  {message.guardrails.blockedReasons?.length
                    ? `: ${message.guardrails.blockedReasons.join(', ')}`
                    : ''}
                </span>
              </div>
            )}
            {message.guardrails.inputPiiMasked && (
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px]">
                <ShieldCheck size={11} />
                <span>PII detected in your message and redacted before processing</span>
              </div>
            )}
            {message.guardrails.outputPiiMasked && (
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px]">
                <ShieldCheck size={11} />
                <span>PII detected in the response and redacted</span>
              </div>
            )}
          </div>
        )}

        {/* Query type badge — hidden for clarification responses */}
        {!isUser && !isClarification && message.queryType && (
          <div className="mt-1">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-gray-200 text-gray-600 capitalize">
              {message.queryType.replace('_', ' ')}
            </span>
          </div>
        )}

        {/* Clarifying questions */}
        {!isUser && message.clarifyingQuestions && message.clarifyingQuestions.length > 0 && (
          <ClarifyingQuestions
            questions={message.clarifyingQuestions}
            onQuestionClick={(question) => {
              if (onClarifyingQuestionClick) {
                onClarifyingQuestionClick(question);
              } else {
                setSuggestedInput(question);
              }
            }}
          />
        )}

        {/* Sources — collapsible panel */}
        {!isUser && !isClarification && message.sources && message.sources.length > 0 && (
          <div className="mt-1">
            <button
              onClick={() => setSourcesExpanded(!sourcesExpanded)}
              className="flex items-center gap-1 text-[10px] font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors"
            >
              {sourcesExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
              <ExternalLink size={9} />
              Sources ({message.sources.length})
            </button>
            {sourcesExpanded && (
              <div className="grid grid-cols-2 gap-1 mt-1">
                {message.sources.map((source, idx) => (
                  <SourceCard
                    key={`${source.path}-${idx}`}
                    source={source}
                    index={idx + 1}
                    onClick={() => {
                      if (viewMode !== 'split') {
                        setViewMode('split');
                      }
                      setSelectedSource(source);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pipeline trace — collapsible, mirrors the Issue Analysis stage view */}
        {!isUser && !isClarification && message.pipeline && message.pipeline.length > 0 && (
          <div className="mt-1">
            <button
              onClick={() => setPipelineExpanded(!pipelineExpanded)}
              className="flex items-center gap-1 text-[10px] font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors"
            >
              {pipelineExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
              Pipeline (
              {message.pipeline
                .filter((s) => typeof s.ms === 'number')
                .reduce((a, s) => a + (s.ms ?? 0), 0)}
              ms)
            </button>
            {pipelineExpanded && (
              <div className="mt-1 space-y-0.5">
                {(() => {
                  const timed = message.pipeline!.filter((s) => typeof s.ms === 'number');
                  const maxMs = Math.max(1, ...timed.map((s) => s.ms ?? 0));
                  return message.pipeline!.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px] text-gray-600">
                      <span className="w-44 truncate" title={s.stage}>
                        {s.stage.replace(/_/g, ' ')}
                      </span>
                      {typeof s.ms === 'number' ? (
                        <>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded overflow-hidden">
                            <div
                              className="h-full bg-indigo-400 rounded"
                              style={{ width: `${Math.max(2, ((s.ms ?? 0) / maxMs) * 100)}%` }}
                            />
                          </div>
                          <span className="w-14 text-right tabular-nums">{s.ms} ms</span>
                        </>
                      ) : (
                        <span className="flex-1 text-gray-400">
                          {s.scope_widened_used
                            ? 'search widened to full library'
                            : s.scope_widened_checked
                            ? 'verified against full library'
                            : 'ok'}
                        </span>
                      )}
                      {typeof s.candidates === 'number' && (
                        <span className="text-gray-400">{s.candidates} cand.</span>
                      )}
                      {typeof s.scoped_modules === 'number' && s.scoped_modules > 0 && (
                        <span className="text-gray-400">{s.scoped_modules} mod.</span>
                      )}
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}

        {/* Feedback buttons — hidden for clarification responses */}
        {!isUser && !isClarification && !message.isLoading && (
          <FeedbackButtons messageId={message.id} currentFeedback={message.feedbackGiven} />
        )}
      </div>
    </div>
  );
}
